// @ts-check
// Configurações > API e webhooks > Referência da API (contrato 08 da API; doc 14, seção 6.4): navega pela
// especificação OpenAPI gerada dos metadados da escola (objetos e campos personalizados incluídos), sem biblioteca
// externa. Cada objeto mostra os endpoints, os parâmetros, os campos e um exemplo de chamada.
import { html } from 'htm/preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { PageCard, PageHeader } from '../page/page-card.js';
import { SettingsSection } from './settings-pages.js';
import { CopyableValue } from './settings-channels-page.js';
import { AdminOnlyNote, CodeSnippet } from './integration-ui.js';
import { fetchOpenApiSpec } from '../../data/integrations.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { Link } from '../../app/router.js';
import { downloadBlob } from '../../lib/csv.js';
import { Button } from '../../ui/button.js';
import { Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import { buildCurlExample, describeSchemaType, groupEndpointsByTag } from '../../lib/api-reference.js';

const SCHEMAS = /** @type {const} */ (['core', 'metadata']);

/** @typedef {import('../../lib/api-reference.js').Endpoint} Endpoint */

/** @param {{ method: string }} props */
function MethodBadge({ method }) {
  return html`<span class="api-reference-method" data-method=${method}>${method.toUpperCase()}</span>`;
}

/**
 * @param {{ endpoint: Endpoint, spec: any, baseUrl: string }} props
 */
function EndpointItem({ endpoint, spec, baseUrl }) {
  const parameters = /** @type {any[]} */ (endpoint.operation.parameters ?? []);
  return html`<details class="api-reference-endpoint">
    <summary>
      <${MethodBadge} method=${endpoint.method} />
      <code class="api-reference-path">${endpoint.path}</code>
      <span class="api-reference-summary">${endpoint.operation.summary ?? ''}</span>
    </summary>
    <div class="api-reference-endpoint-body">
      ${endpoint.operation.description ? html`<p class="api-reference-description">${endpoint.operation.description}</p>` : null}
      ${parameters.length > 0
        ? html`<div class="api-reference-parameters">
            <span class="form-field-label">${t('apiReference.parameters')}</span>
            ${parameters.map((parameter) => html`<div key=${parameter.name} class="api-reference-parameter">
              <div class="api-reference-parameter-head">
                <code class="api-reference-parameter-name">${parameter.name}</code>
                ${parameter.required ? html`<span class="form-field-required">*</span>` : null}
                <span class="api-reference-parameter-meta">${parameter.in}</span>
                <code class="api-reference-parameter-meta">${describeSchemaType(parameter.schema)}</code>
              </div>
              ${parameter.description ? html`<p class="api-reference-parameter-description">${parameter.description}</p>` : null}
            </div>`)}
          </div>`
        : null}
      <${CodeSnippet} label=${t('apiReference.example')} code=${buildCurlExample({ baseUrl, endpoint, spec })} />
    </div>
  </details>`;
}

/**
 * @param {{ spec: any, schemaName: string }} props
 */
function FieldTable({ spec, schemaName }) {
  const schemas = spec?.components?.schemas ?? {};
  const response = schemas[`${schemaName}ForResponse`]?.properties ?? {};
  const writable = schemas[`${schemaName}ForUpdate`]?.properties ?? {};
  const required = new Set(schemas[schemaName]?.required ?? []);
  return html`<div class="settings-table api-reference-table">
    <div class="settings-table-row settings-table-header" data-columns="api-fields">
      <span>${t('apiReference.field')}</span><span>${t('apiReference.type')}</span><span>${t('apiReference.access')}</span><span>${t('apiReference.details')}</span>
    </div>
    ${Object.entries(response).map(([name, property]) => html`<div key=${name} class="settings-table-row" data-columns="api-fields">
      <span class="settings-table-mono">${name}${required.has(name) ? html`<span class="form-field-required">*</span>` : null}</span>
      <span class="settings-table-muted settings-table-mono integration-ellipsis" title=${describeSchemaType(property)}>${describeSchemaType(property)}</span>
      <span class="settings-table-muted">${writable[name] ? t('apiReference.readWrite') : t('apiReference.readOnly')}</span>
      <span class="settings-table-muted integration-ellipsis" title=${/** @type {any} */ (property).description ?? ''}>${translateMetadataLabel(/** @type {any} */ (property).description ?? '')}</span>
    </div>`)}
    ${Object.entries(writable).filter(([name]) => !(name in response)).map(([name, property]) => html`<div key=${name} class="settings-table-row" data-columns="api-fields">
      <span class="settings-table-mono">${name}</span>
      <span class="settings-table-muted settings-table-mono">${describeSchemaType(property)}</span>
      <span class="settings-table-muted">${t('apiReference.writeOnly')}</span>
      <span class="settings-table-muted integration-ellipsis">${/** @type {any} */ (property).description ?? ''}</span>
    </div>`)}
  </div>`;
}

export function SettingsApiReferencePage() {
  const [schema, setSchema] = useState(/** @type {'core' | 'metadata'} */ ('core'));
  const [spec, setSpec] = useState(/** @type {any} */ (null));
  const [query, setQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState(/** @type {string | null} */ (null));
  useEffect(() => {
    document.title = `${t('apiReference.title')} - ${t('navigation.settings')}`;
  }, []);
  useEffect(() => {
    setSpec(null);
    fetchOpenApiSpec(schema).then(setSpec).catch((error) => {
      showErrorSnackbar(error);
      setSpec({ paths: {} });
    });
  }, [schema]);

  const groups = useMemo(
    () => groupEndpointsByTag(spec).sort((first, second) => translateMetadataLabel(first.tag).localeCompare(translateMetadataLabel(second.tag), 'pt-BR')),
    [spec],
  );
  const normalizedQuery = query.trim().toLowerCase();
  const visibleGroups = groups.filter((group) => !normalizedQuery
    || translateMetadataLabel(group.tag).toLowerCase().includes(normalizedQuery)
    || group.endpoints.some((endpoint) => endpoint.path.toLowerCase().includes(normalizedQuery)));
  const selected = visibleGroups.find((group) => group.tag === selectedTag) ?? visibleGroups[0] ?? null;
  const baseUrl = spec?.servers?.[0]?.url ?? `${window.location.origin}/rest`;

  const breadcrumb = html`<nav class="record-breadcrumb">
    <${Link} href="/settings/developers" className="record-breadcrumb-prefix">${t('leadCapture.developers.title')}<//>
    <span class="record-breadcrumb-separator">/</span>
    <span class="record-breadcrumb-current">${t('apiReference.title')}</span>
  </nav>`;
  if (!hasCurrentPermissionFlag('API_KEYS_AND_WEBHOOKS')) {
    return html`<${PageCard} header=${html`<${PageHeader} breadcrumb=${breadcrumb} />`}><div class="settings-page"><${AdminOnlyNote} /></div><//>`;
  }

  return html`<${PageCard} header=${html`<${PageHeader} breadcrumb=${breadcrumb} />`}>
    <div class="settings-page api-reference-page">
      <${SettingsSection} title=${t('apiReference.title')} description=${t('apiReference.description')}>
        <div class="api-reference-intro">
          <div class="form-field">
            <span class="form-field-label">${t('apiReference.baseUrl')}</span>
            <${CopyableValue} value=${baseUrl} label=${t('leadCapture.common.copy')} />
            <span class="input-hint">${t('apiReference.authHint')}</span>
          </div>
          <div class="api-reference-actions">
            <div class="api-reference-switch" role="tablist" aria-label=${t('apiReference.schema')}>
              ${SCHEMAS.map((option) => html`<button key=${option} type="button" role="tab" aria-selected=${schema === option}
                class=${`api-reference-switch-option${schema === option ? ' is-active' : ''}`} onClick=${() => {
                  setSchema(option);
                  setSelectedTag(null);
                }}>${t(`apiReference.schemas.${option}`)}</button>`)}
            </div>
            <${Button} title=${t('apiReference.download')} icon="IconDownload" variant="secondary" size="small" disabled=${!spec}
              onClick=${() => downloadBlob(new Blob([JSON.stringify(spec, null, 2)], { type: 'application/json' }), `openapi-${schema}.json`)} />
          </div>
        </div>
      <//>
      ${spec === null
        ? html`<${Skeleton} height=${240} radius=${8} />`
        : html`<div class="api-reference-layout">
            <aside class="api-reference-sidebar">
              <label class="api-reference-search">
                <${Icon} name="IconSearch" size=${14} />
                <input class="text-input" type="search" value=${query} placeholder=${t('apiReference.searchPlaceholder')} aria-label=${t('apiReference.searchPlaceholder')}
                  onInput=${(/** @type {Event} */ event) => setQuery(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
              </label>
              <nav class="api-reference-tags" aria-label=${t('apiReference.objects')}>
                ${visibleGroups.map((group) => html`<button key=${group.tag} type="button"
                  class=${`api-reference-tag${selected?.tag === group.tag ? ' is-active' : ''}`} onClick=${() => setSelectedTag(group.tag)}>
                  <span class="integration-ellipsis">${translateMetadataLabel(group.tag)}</span>
                  <span class="api-reference-count">${group.endpoints.length}</span>
                </button>`)}
                ${visibleGroups.length === 0 ? html`<div class="integration-empty">${t('apiReference.empty')}</div>` : null}
              </nav>
            </aside>
            <section class="api-reference-content">
              ${selected
                ? html`<h2 class="api-reference-heading">${translateMetadataLabel(selected.tag)}</h2>
                  <div class="api-reference-endpoints">
                    ${selected.endpoints.map((endpoint) => html`<${EndpointItem} key=${`${endpoint.method} ${endpoint.path}`} endpoint=${endpoint} spec=${spec} baseUrl=${baseUrl} />`)}
                  </div>
                  ${selected.schemaName
                    ? html`<h3 class="api-reference-subheading">${t('apiReference.fields')}</h3><${FieldTable} spec=${spec} schemaName=${selected.schemaName} />`
                    : null}`
                : null}
            </section>
          </div>`}
    </div>
  <//>`;
}
